import { ArrowUpRight, TriangleAlert } from "lucide-react";
import { Button, Tabs, DateTimeText } from "@nexus/ui";
import type { DrawerCallbacks, DrawerDescriptor, JsonValue } from "@nexus/widget-sdk";

import { parseDbTime } from "@nexus/widget-kit";
import { formatCount, formatPercent } from "./format";
import { summarizeEvent, type MonitoredEvent, type MonitoringSegment } from "./model";
import { Sparkline } from "./Sparkline";

type Anomaly = MonitoringSegment["anomalies"][number];

/** Event detail panel: the figures first, then trend and anomalies, then per-channel detail. */
export function renderDetail(
  payload: JsonValue,
  callbacks: DrawerCallbacks,
): DrawerDescriptor | null {
  const input = payload as unknown as { event?: MonitoredEvent; fetchedAt?: string };
  const event = input?.event;
  if (!event || !Array.isArray(event.segments)) return null;
  const summary = summarizeEvent(event, Date.now());
  const anomalies = Array.from(
    new Map(
      event.segments
        .flatMap((segment) => segment.anomalies)
        .map((anomaly) => [anomaly.anomaly_id, anomaly]),
    ).values(),
  ).sort(
    (a, b) =>
      Number(a.anomaly_status === "resolved") - Number(b.anomaly_status === "resolved") ||
      parseDbTime(b.raised_at) - parseDbTime(a.raised_at),
  );
  const open = (protectedEventId?: string) =>
    callbacks.onInteraction("event.monitoring.open", {
      eventId: event.event_id,
      ...(protectedEventId ? { protectedEventId } : {}),
    });

  return {
    title: event.event_label,
    subtitle: (
      <>
        {event.event_type} ·{" "}
        <DateTimeText
          value={parseDbTime(event.event_start)}
          options={{ hour: "2-digit", minute: "2-digit", hourCycle: "h23" }}
        />
        –
        <DateTimeText
          value={parseDbTime(event.event_end)}
          options={{ hour: "2-digit", minute: "2-digit", hourCycle: "h23" }}
        />
      </>
    ),
    badges: event.unresolved_anomaly_count ? (
      <span className="inline-flex items-center gap-1 rounded-full bg-status-error-bg px-2 py-0.5 text-xs font-semibold text-status-error-text">
        <TriangleAlert className="size-3" aria-hidden />
        {event.unresolved_anomaly_count} open
      </span>
    ) : (
      <span className="text-xs text-muted">{summary.status}</span>
    ),
    footer: (
      <Button variant="primary" onClick={() => open()}>
        Open Event Monitor
        <ArrowUpRight className="size-4" aria-hidden />
      </Button>
    ),
    children: (
      <Tabs
        key={event.event_id}
        label="Event detail views"
        defaultValue="overview"
        items={[
          {
            value: "overview",
            label: "Overview",
            content: (
              <div className="space-y-7 py-2">
                <dl className="grid grid-cols-3 gap-4 rounded-panel bg-surface-elevated/60 p-4">
                  <Figure label="Detections" value={formatCount(summary.detections)} />
                  <Figure label="Response" value={formatPercent(summary.responseRate)} />
                  <Figure
                    label="Targets reached"
                    value={summary.targets ? `${summary.reached}/${summary.targets}` : "—"}
                  />
                </dl>
                <section>
                  <h3 className="mb-3 text-sm font-semibold text-primary">Detection trend</h3>
                  <Sparkline segments={event.segments} expanded />
                  {summary.partial || summary.older ? (
                    <p className="mt-2 text-xs text-muted">
                      {summary.partial ? "Partial data" : "Latest data older than expected"}
                    </p>
                  ) : null}
                </section>
                <section aria-label="Event anomalies">
                  <h3 className="mb-1 text-sm font-semibold text-primary">
                    Anomalies{" "}
                    <span className="font-normal text-muted">
                      · {event.unresolved_anomaly_count} open
                    </span>
                  </h3>
                  {anomalies.length ? (
                    <div className="divide-y divide-border">
                      {anomalies.slice(0, 5).map((anomaly) => (
                        <AnomalyRow key={anomaly.anomaly_id} anomaly={anomaly} />
                      ))}
                    </div>
                  ) : (
                    <p className="py-2 text-sm text-muted">None reported</p>
                  )}
                </section>
                <p className="text-xs text-muted">
                  Snapshot ·{" "}
                  <DateTimeText value={input.fetchedAt ? parseDbTime(input.fetchedAt) : null} />
                </p>
              </div>
            ),
          },
          {
            value: "channels",
            label: "Channels",
            content: (
              <div className="divide-y divide-border py-2">
                {event.protected_events.map((channel) => {
                  const segments = event.segments.filter(
                    (segment) => String(segment.protectedEventId) === channel.protected_event_id,
                  );
                  const detections = segments.reduce<number | null>(
                    (sum, segment) =>
                      segment.detections === null ? sum : (sum ?? 0) + segment.detections,
                    null,
                  );
                  return (
                    <button
                      key={channel.protected_event_id}
                      type="button"
                      onClick={() => open(channel.protected_event_id)}
                      className="flex w-full items-center gap-3 px-1 py-3 text-left hover:bg-surface-elevated/60 focus-visible:ring-2 focus-visible:ring-focus focus-visible:outline-none"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-primary">
                          {channel.channel}
                        </span>
                        <span className="block truncate text-xs text-muted">
                          {channel.tenant_label}
                        </span>
                      </span>
                      <span className="w-20 shrink-0">
                        <Sparkline segments={segments} />
                      </span>
                      <span className="w-14 shrink-0 text-right text-sm font-semibold tabular-nums text-primary">
                        {formatCount(detections)}
                      </span>
                      {channel.unresolved_anomaly_count ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-status-error-text">
                          <TriangleAlert className="size-3" aria-hidden />
                          {channel.unresolved_anomaly_count}
                        </span>
                      ) : null}
                      <ArrowUpRight className="size-4 shrink-0 text-muted" aria-hidden />
                    </button>
                  );
                })}
                {!event.protected_events.length && (
                  <p className="py-3 text-sm text-muted">No protected channels in this scope</p>
                )}
              </div>
            ),
          },
        ]}
      />
    ),
  };
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted">{label}</dt>
      <dd className="mt-1 text-xl font-semibold tabular-nums text-primary">{value}</dd>
    </div>
  );
}

function AnomalyRow({ anomaly }: { anomaly: Anomaly }) {
  return (
    <div className="py-2.5">
      <p className="text-sm font-medium text-primary">
        {anomaly.anomaly_type}{" "}
        <span className="text-xs font-normal text-muted">
          · {anomaly.anomaly_severity} · {anomaly.anomaly_status} ·{" "}
          <DateTimeText value={parseDbTime(anomaly.raised_at)} />
        </span>
      </p>
      {anomaly.anomaly_description ? (
        <p className="mt-0.5 text-sm text-secondary">{anomaly.anomaly_description}</p>
      ) : null}
    </div>
  );
}
